import {
  InfiniteData,
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import {
  ExtensionInstance,
  ExtensionPreview,
  ExtensionType,
  PaginatedApiResult,
} from '@mangovoice/types'
import { ApiError } from '@mangovoice/types'

interface GetPbxLineExtensionsParams {
  pbxId: string | number
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ExtensionPreview>, ApiError>,
    'queryKey'
  >
}
export type ExtensionTypeValue = `${ExtensionType}`
const extensionTypePrefix = 'Extension'
export type ExtensionTypeLabel =
  `${ExtensionType extends `${typeof extensionTypePrefix}${infer R}` ? R : ExtensionType}`
export const extensionType = (type: ExtensionTypeLabel): ExtensionTypeValue =>
  `${extensionTypePrefix}${type}`

interface GetExtensionsParams {
  type?: ExtensionTypeLabel
  search?: string
  options?: Omit<
    UseInfiniteQueryOptions<
      PaginatedApiResult<ExtensionInstance>,
      ApiError,
      InfiniteData<PaginatedApiResult<ExtensionInstance>>
    >,
    'queryKey'
  >
}
const pageSize = 100

export const useGetPbxLineExtensions = ({
  pbxId,
  options,
}: GetPbxLineExtensionsParams) => {
  const api = useMangoApiClient()
  const query = useQuery<PaginatedApiResult<ExtensionPreview>, ApiError>({
    queryKey: ['pbx_line_extensions', pbxId],
    queryFn: async () => {
      const { ok, data } = await api.extensions.getAllExtensions({
        pbx_id: pbxId as number,
        type: extensionType('Line'),
        limit: 999,
      })

      if (!ok) {
        return Promise.reject(data as ApiError)
      }
      const extensions = data as PaginatedApiResult<ExtensionPreview>
      return extensions
    },
    ...options,

    gcTime: options?.gcTime ?? 15 * 60 * 1000,
    retry: false,
  })

  return {
    ...query,
  }
}

export const useGetExtensions = ({
  type,
  search,
  options,
}: GetExtensionsParams) => {
  const { pbxId } = useUserState()
  const api = useMangoApiClient()
  const queryKey = useMemo(() => {
    const key = ['extensions', pbxId, `type-${type}`, search]
    return key
  }, [pbxId, type, search])

  const queryFn: QueryFunction<
    PaginatedApiResult<ExtensionInstance>,
    QueryKey,
    unknown
  > = async ({ pageParam = 0 }) => {
    const emptyResult = { count: 0, next: null, previous: null, results: [] }
    if (pbxId === 0) {
      return Promise.resolve(emptyResult)
    }
    const offset = (pageParam ?? 0) as number
    const { ok, data } = await api.extensions.listLegacy({
      pbx_id: pbxId,
      offset,
      limit: pageSize,
      type: type ? extensionType(type) : undefined,
      search,
    })

    if (!ok || !data) {
      return Promise.reject(data as ApiError)
    }

    return data
  }

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    ...options,
    initialPageParam: 0,
    retry: (options?.retry as number | boolean | undefined) ?? false,
    getNextPageParam: (lastPage) => {
      if (lastPage.next === null) {
        return null
      }
      return getPaginatedResultParts(lastPage)?.nextPageOffset ?? null
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.previous === null) {
        return null
      }
      return getPaginatedResultParts(firstPage)?.previousPageOffset ?? null
    },
  })

  return {
    ...query,
    results: query.data?.pages.flatMap((page) => page.results) ?? [],
  }
}
